import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { loginSuccess } from '../../store/slices/auth.slice';
import authApi from '../../services/api/auth.api';
import planApi, { Plan } from '../../services/api/plan.api';
import LanguageSwitcher from '../../components/common/LanguageSwitcher';
import { useWhiteLabel } from '../../contexts/WhiteLabelContext';
import {
  ChartBarIcon,
  EnvelopeIcon,
  LockClosedIcon,
  UserIcon,
  BuildingOfficeIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  CheckIcon,
  EyeIcon,
  EyeSlashIcon,
  PhoneIcon,
  GlobeAltIcon,
  SparklesIcon,
  BoltIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';

type BillingCycle = 'monthly' | 'yearly';

export default function RegisterPage() {
  const { t, i18n } = useTranslation(['auth', 'plans']);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { config: whiteLabelConfig, shouldShowWhiteLabel } = useWhiteLabel();
  const [searchParams] = useSearchParams();
  // Plan vindo do /pricing (ex: /register?plan=pro&billing=yearly).
  // Pré-seleciona quando os plans carregarem e pula direto pro step 2 (form).
  const planSlugFromUrl = (searchParams.get('plan') || '').toLowerCase().trim();
  const billingFromUrl = (searchParams.get('billing') || '').toLowerCase().trim();

  // Multi-step form state
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    tenantName: '',
    phone: '',
  });
  const [isLoading, setIsLoading] = useState(false);

  // Get currency based on current i18n language
  const currentCurrency = i18n.language?.startsWith('pt') ? 'BRL' : 'USD';

  // Load plans on mount and when language changes
  useEffect(() => {
    loadPlans();
  }, [currentCurrency]);

  const loadPlans = async () => {
    try {
      setLoadingPlans(true);
      const response = await planApi.listPublic(currentCurrency as 'USD' | 'BRL');
      if (response.success) {
        // Sort by sort_order
        const sortedPlans = response.data.sort((a, b) => a.sort_order - b.sort_order);
        setPlans(sortedPlans);

        // Pré-seleção via URL (?plan=professional&billing=yearly). Se bater, pula step 2.
        // Antes era setSelectedPlan(null) sempre — user vindo do /pricing tinha
        // que reclicar no plano que JÁ tinha escolhido.
        if (planSlugFromUrl) {
          // Alias pra compat com links antigos (?plan=pro mapeia pra "professional")
          const aliasMap: Record<string, string> = { pro: 'professional', biz: 'business', ent: 'enterprise' };
          const normalizedSlug = aliasMap[planSlugFromUrl] || planSlugFromUrl;
          const match = sortedPlans.find(p =>
            String(p.slug || '').toLowerCase() === normalizedSlug ||
            String(p.name || '').toLowerCase() === normalizedSlug
          );
          if (match) {
            setSelectedPlan(match);
            if (billingFromUrl === 'yearly' || billingFromUrl === 'annual') {
              setBillingCycle('yearly');
            }
            setCurrentStep(2);
          } else {
            setSelectedPlan(null);
          }
        } else {
          setSelectedPlan(null);
        }
      }
    } catch (error) {
      console.error('Failed to load plans:', error);
      toast.error(t('auth:register.failedToLoadPlans'), { icon: '❌' });
    } finally {
      setLoadingPlans(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handlePlanSelect = (plan: Plan) => {
    setSelectedPlan(plan);
    setCurrentStep(2);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedPlan) {
      toast.error(t('auth:register.selectPlan'), { icon: '⚠️' });
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error(t('auth:register.passwordMismatch'), { icon: '⚠️' });
      return;
    }

    if (formData.password.length < 6) {
      toast.error(t('auth:register.passwordTooShort'), { icon: '⚠️' });
      return;
    }

    try {
      setIsLoading(true);

      const registerData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        tenantName: formData.tenantName,
        phone: formData.phone,
        planSlug: selectedPlan.slug,
        billingCycle: billingCycle,
      };

      const response = await authApi.register(registerData);

      if (response.success) {
        // Login the user
        dispatch(
          loginSuccess({
            user: response.data.user,
            token: response.data.token,
            refreshToken: response.data.refreshToken,
          })
        );

        toast.success(t('auth:register.success'), { icon: '✅' });

        // Conversão de cadastro no Google Ads — ação "Inscrição" do flux.
        // Dispara assim que a conta é criada (antes de qualquer redirect).
        try {
          (window as any).gtag?.('event', 'conversion', {
            send_to: 'AW-18102177263/aNZNCICo1sEcEO-b5bdD',
            value: 1.0,
            currency: 'BRL',
          });
        } catch { /* tracking nunca bloqueia o cadastro */ }

        // Limpar caches e sessionStorage para garantir sessão limpa
        if ('caches' in window) {
          caches.keys().then((names) => {
            names.forEach((name) => {
              caches.delete(name);
            });
          });
        }
        sessionStorage.clear();

        // Redirect based on plan
        if (selectedPlan.slug === 'free') {
          // Usar window.location para forçar reload completo
          window.location.href = '/dashboard';
        } else {
          // For paid plans, create Stripe checkout session
          try {
            const paymentResponse = await authApi.setupPayment({
              tenantId: response.data.user.tenantId,
              email: formData.email,
              planSlug: selectedPlan.slug,
              billingCycle: billingCycle,
              currency: currentCurrency as 'USD' | 'BRL',
            });

            if (paymentResponse.success && paymentResponse.data.checkoutUrl) {
              // Redirect to Stripe checkout (já usa window.location)
              window.location.href = paymentResponse.data.checkoutUrl;
            } else {
              toast.error(t('auth:register.paymentSetupFailed'), { icon: '❌' });
              window.location.href = '/dashboard';
            }
          } catch (paymentError) {
            console.error('Payment setup error:', paymentError);
            toast.error(t('auth:register.paymentSetupFailed'), { icon: '❌' });
            window.location.href = '/dashboard';
          }
        }
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.error || t('auth:register.error'), { icon: '❌' });
    } finally {
      setIsLoading(false);
    }
  };

  const getPlanPrice = (plan: Plan) => {
    const price = billingCycle === 'yearly' ? parseFloat(plan.price_annual) : parseFloat(plan.price_monthly);
    return price;
  };

  const formatPrice = (price: number, plan?: Plan) => {
    if (price === 0) return t('plans:free');
    const currency = plan?.currency || 'USD';
    const locale = currency === 'BRL' ? 'pt-BR' : 'en-US';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const getPlanIcon = (slug: string) => {
    switch (slug) {
      case 'free':
        return '🎯';
      case 'starter':
        return '🚀';
      case 'professional':
      case 'pro':
        return '👑';
      case 'enterprise':
        return '💎';
      default:
        return '📊';
    }
  };

  const getTranslatedPlanName = (slug: string) => {
    return t(`plans:plans.${slug}.name`, { defaultValue: slug });
  };

  const getTranslatedPlanDescription = (slug: string) => {
    return t(`plans:plans.${slug}.description`, { defaultValue: '' });
  };

  if (loadingPlans) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">{t('auth:register.loadingPlans')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-gray-50 dark:bg-gray-900">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-indigo-600 via-purple-600 to-indigo-800 p-12 flex-col justify-between relative overflow-hidden">
        {/* Decorative circles */}
        <div className="absolute top-20 right-20 w-72 h-72 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"></div>

        <div className="relative z-10">
          {shouldShowWhiteLabel && whiteLabelConfig.custom_name ? (
            <div className="flex items-center gap-3 text-white mb-12">
              <ChartBarIcon className="h-12 w-12" />
              <span className="text-3xl font-bold">{whiteLabelConfig.custom_name}</span>
            </div>
          ) : (
            <Link to="/" className="flex items-center gap-3 text-white mb-12">
              <ChartBarIcon className="h-12 w-12" />
              <span className="text-3xl font-bold">Kubmix Flux</span>
            </Link>
          )}

          <div className="space-y-8">
            <div>
              <h1 className="text-5xl font-bold text-white mb-4">
                {t('auth:register.hero.title')}
              </h1>
              <p className="text-xl text-indigo-100">
                {t('auth:register.hero.subtitle')}
              </p>
            </div>
          </div>

          {/* Plan Features - Desktop */}
          {currentStep === 2 && selectedPlan && (
            <div className="mt-8 bg-white/15 backdrop-blur-md rounded-xl p-5 border-2 border-white/30 shadow-2xl">
              <h3 className="text-base font-bold text-white mb-3 flex items-center gap-2">
                <SparklesIcon className="h-5 w-5 text-yellow-300" />
                {t('auth:register.planFeatures')}
              </h3>

              <div className="bg-white/20 rounded-lg p-3 mb-3 border border-white/20">
                <div className="text-indigo-100 text-xs uppercase tracking-wider mb-1">
                  {t('auth:register.selectedPlan')}
                </div>
                <div className="text-white text-xl font-extrabold mb-1">
                  {getTranslatedPlanName(selectedPlan.slug)}
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="text-white text-2xl font-bold">
                    {formatPrice(getPlanPrice(selectedPlan), selectedPlan)}
                  </span>
                  <span className="text-indigo-100 text-sm">
                    /{billingCycle === 'monthly' ? t('plans:month') : t('plans:year')}
                  </span>
                </div>
                {selectedPlan.slug !== 'free' && (
                  <div className="mt-2 pt-2 border-t border-white/20">
                    <div className="text-green-300 text-xs font-semibold">
                      {t('auth:register.trialInfo')}
                    </div>
                  </div>
                )}
              </div>

              {/* Features */}
              <div className="space-y-2">
                <div className="font-bold text-white text-xs uppercase tracking-wider flex items-center gap-2">
                  <div className="w-1 h-3 bg-green-300 rounded-full"></div>
                  {t('auth:register.includedFeatures')}
                </div>
                <div className="grid grid-cols-1 gap-2">
                  {selectedPlan.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-2 bg-white/10 rounded-lg p-2">
                      <CheckIcon className="h-4 w-4 text-green-300 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-white">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Testimonial */}
        <div className="relative z-10 bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
          <p className="text-white text-lg italic mb-4">
            "{t('auth:register.hero.testimonial')}"
          </p>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-white font-bold">
              JS
            </div>
            <div>
              <div className="text-white font-semibold">John Smith</div>
              <div className="text-indigo-200 text-sm">CTO, TechCorp</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Register Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 overflow-y-auto relative">
        {/* Language Switcher */}
        <div className="absolute top-6 right-6 z-10">
          <LanguageSwitcher />
        </div>

        {/* Back to Login */}
        <Link
          to="/login"
          className="absolute top-6 left-6 inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-all border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 z-10"
        >
          <ArrowLeftIcon className="h-5 w-5" />
          <span className="text-sm font-medium">{t('auth:register.backToLogin')}</span>
        </Link>

        <div className="w-full max-w-md pt-20">
          {/* Mobile Logo */}
          <div className="flex lg:hidden items-center justify-center gap-3 mb-8">
            <ChartBarIcon className="h-12 w-12 text-indigo-600" />
            <span className="text-3xl font-bold text-gray-900 dark:text-white">
              {shouldShowWhiteLabel && whiteLabelConfig.custom_name ? whiteLabelConfig.custom_name : 'Kubmix Flux'}
            </span>
          </div>

          {/* Progress Steps */}
          <div className="mb-8">
            <div className="flex items-center justify-center gap-4">
              <div className="flex items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${
                    currentStep >= 1
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                  }`}
                >
                  {currentStep > 1 ? <CheckIcon className="h-6 w-6" /> : '1'}
                </div>
                <div className="w-24 h-1 bg-gray-200 dark:bg-gray-700 mx-2 relative overflow-hidden">
                  <div
                    className={`h-full transition-all duration-500 bg-indigo-600 ${
                      currentStep >= 2 ? 'w-full' : 'w-0'
                    }`}
                  ></div>
                </div>
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${
                    currentStep >= 2
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                  }`}
                >
                  2
                </div>
              </div>
            </div>
            <div className="flex justify-center gap-16 mt-3">
              <span
                className={`text-sm font-medium ${
                  currentStep === 1 ? 'text-indigo-600' : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                {t('auth:register.step1Title')}
              </span>
              <span
                className={`text-sm font-medium ${
                  currentStep === 2 ? 'text-indigo-600' : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                {t('auth:register.step2Title')}
              </span>
            </div>
          </div>

          {/* STEP 1: Plan Selection */}
          {currentStep === 1 && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 border border-gray-100 dark:border-gray-700">
              <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                  {t('auth:register.choosePlanTitle')}
                </h1>
                <p className="text-gray-600 dark:text-gray-400">{t('auth:register.choosePlanSubtitle')}</p>
              </div>

              {/* Billing Cycle Toggle */}
              <div className="flex items-center justify-center gap-4 mb-6">
                <span
                  className={`text-sm font-medium ${
                    billingCycle === 'monthly'
                      ? 'text-indigo-600'
                      : 'text-gray-600 dark:text-gray-400'
                  }`}
                >
                  {t('plans:monthly')}
                </span>
                <button
                  type="button"
                  onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                    billingCycle === 'yearly' ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      billingCycle === 'yearly' ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
                <span
                  className={`text-sm font-medium ${
                    billingCycle === 'yearly'
                      ? 'text-indigo-600'
                      : 'text-gray-600 dark:text-gray-400'
                  }`}
                >
                  {t('plans:yearly')}
                  <span className="ml-2 text-xs bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-2 py-0.5 rounded-full">
                    {t('plans:save20')}
                  </span>
                </span>
              </div>

              {/* Plans List - Vertical */}
              <div className="space-y-3">
                {plans.map((plan) => (
                  <button
                    key={plan.id}
                    type="button"
                    onClick={() => handlePlanSelect(plan)}
                    className={`w-full cursor-pointer bg-white dark:bg-gray-800 rounded-xl p-4 border-2 transition-all hover:shadow-lg text-left ${
                      selectedPlan?.id === plan.id
                        ? 'border-indigo-600 shadow-md'
                        : 'border-gray-200 dark:border-gray-700 hover:border-indigo-300'
                    } ${plan.slug === 'professional' || plan.slug === 'pro' ? 'ring-2 ring-purple-300' : ''}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div
                          className={`w-12 h-12 rounded-lg flex items-center justify-center text-2xl ${
                            plan.slug === 'free'
                              ? 'bg-gray-100 dark:bg-gray-700'
                              : plan.slug === 'starter'
                              ? 'bg-blue-100 dark:bg-blue-900/30'
                              : plan.slug === 'professional' || plan.slug === 'pro'
                              ? 'bg-purple-100 dark:bg-purple-900/30'
                              : 'bg-indigo-100 dark:bg-indigo-900/30'
                          }`}
                        >
                          {getPlanIcon(plan.slug)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-lg text-gray-900 dark:text-white">
                              {getTranslatedPlanName(plan.slug)}
                            </span>
                            {(plan.slug === 'professional' || plan.slug === 'pro') && (
                              <span className="bg-purple-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                                {t('auth:register.popular')}
                              </span>
                            )}
                          </div>
                          <p className="text-gray-600 dark:text-gray-400 text-sm">
                            {getTranslatedPlanDescription(plan.slug)}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-extrabold text-gray-900 dark:text-white">
                          {formatPrice(getPlanPrice(plan), plan)}
                        </div>
                        <div className="text-gray-500 dark:text-gray-400 text-sm">
                          /{billingCycle === 'monthly' ? t('plans:month') : t('plans:year')}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* STEP 2: Account Details */}
          {currentStep === 2 && (
            <div className="space-y-6">
              {/* Plan Features Card - Mobile */}
              {selectedPlan && (
                <div className="lg:hidden bg-gradient-to-br from-indigo-600 to-purple-600 rounded-2xl p-5 text-white shadow-2xl">
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <SparklesIcon className="h-5 w-5 text-yellow-300" />
                    {t('auth:register.planFeatures')}
                  </h3>

                  <div className="bg-white/20 rounded-xl p-4 mb-4 border border-white/20">
                    <div className="text-indigo-100 text-xs uppercase tracking-wider mb-1">
                      {t('auth:register.selectedPlan')}
                    </div>
                    <div className="text-white text-xl font-extrabold mb-1">
                      {getTranslatedPlanName(selectedPlan.slug)}
                    </div>
                    <div className="flex items-baseline gap-1">
                      <span className="text-white text-2xl font-bold">
                        {formatPrice(getPlanPrice(selectedPlan), selectedPlan)}
                      </span>
                      <span className="text-indigo-100 text-sm">
                        /{billingCycle === 'monthly' ? t('plans:month') : t('plans:year')}
                      </span>
                    </div>
                    {selectedPlan.slug !== 'free' && (
                      <div className="mt-2 pt-2 border-t border-white/20">
                        <div className="text-green-300 text-xs font-semibold">
                          {t('auth:register.trialInfo')}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Features */}
                  <div className="space-y-2">
                    <div className="font-bold text-white text-xs uppercase tracking-wider flex items-center gap-2">
                      <div className="w-1 h-3 bg-green-300 rounded-full"></div>
                      {t('auth:register.includedFeatures')}
                    </div>
                    <div className="grid grid-cols-1 gap-2">
                      {selectedPlan.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-2 bg-white/10 rounded-lg p-2">
                          <CheckIcon className="h-4 w-4 text-green-300 flex-shrink-0 mt-0.5" />
                          <span className="text-sm font-medium">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 border border-gray-100 dark:border-gray-700">
                <div className="mb-6">
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                    {t('auth:register.createAccount')}
                  </h1>
                  <p className="text-gray-600 dark:text-gray-400">{t('auth:register.subtitle')}</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Name Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        {t('auth:register.firstName')} *
                      </label>
                      <div className="relative">
                        <UserIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                        <input
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          required
                          className="w-full pl-12 pr-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl focus:ring-2 focus:ring-indigo-600 focus:border-transparent transition-all"
                          placeholder={t('auth:register.firstNamePlaceholder')}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        {t('auth:register.lastName')} *
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl focus:ring-2 focus:ring-indigo-600 focus:border-transparent transition-all"
                        placeholder={t('auth:register.lastNamePlaceholder')}
                      />
                    </div>
                  </div>

                  {/* Email & Phone */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        {t('auth:register.workEmail')} *
                      </label>
                      <div className="relative">
                        <EnvelopeIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full pl-12 pr-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl focus:ring-2 focus:ring-indigo-600 focus:border-transparent transition-all"
                          placeholder={t('auth:register.emailPlaceholder')}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        {t('auth:register.phone')}
                      </label>
                      <div className="relative">
                        <PhoneIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full pl-12 pr-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl focus:ring-2 focus:ring-indigo-600 focus:border-transparent transition-all"
                          placeholder={t('auth:register.phonePlaceholder')}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Company Name */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      {t('auth:register.companyName')} *
                    </label>
                    <div className="relative">
                      <BuildingOfficeIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                      <input
                        type="text"
                        name="tenantName"
                        value={formData.tenantName}
                        onChange={handleChange}
                        required
                        className="w-full pl-12 pr-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl focus:ring-2 focus:ring-indigo-600 focus:border-transparent transition-all"
                        placeholder={t('auth:register.companyNamePlaceholder')}
                      />
                    </div>
                  </div>

                  {/* Password Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        {t('auth:register.password')} *
                      </label>
                      <div className="relative">
                        <LockClosedIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                        <input
                          type={showPassword ? 'text' : 'password'}
                          name="password"
                          value={formData.password}
                          onChange={handleChange}
                          required
                          minLength={6}
                          className="w-full pl-12 pr-12 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl focus:ring-2 focus:ring-indigo-600 focus:border-transparent transition-all"
                          placeholder={t('auth:register.passwordPlaceholder')}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        >
                          {showPassword ? (
                            <EyeSlashIcon className="h-5 w-5" />
                          ) : (
                            <EyeIcon className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        {t('auth:register.confirmPassword')} *
                      </label>
                      <div className="relative">
                        <LockClosedIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                        <input
                          type={showPassword ? 'text' : 'password'}
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          required
                          minLength={6}
                          className="w-full pl-12 pr-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl focus:ring-2 focus:ring-indigo-600 focus:border-transparent transition-all"
                          placeholder={t('auth:register.confirmPasswordPlaceholder')}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Payment Notice */}
                  {selectedPlan && selectedPlan.slug !== 'free' && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <CreditCardIcon className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5 h-5 w-5" />
                        <div>
                          <p className="font-semibold text-blue-900 dark:text-blue-100">
                            {t('auth:register.paymentInfo')}
                          </p>
                          <p className="text-sm text-blue-700 dark:text-blue-300">
                            {t('auth:register.paymentInfoDesc')}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Buttons */}
                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setCurrentStep(1)}
                      className="flex items-center justify-center gap-2 px-6 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 font-semibold transition-all"
                    >
                      <ArrowLeftIcon className="h-5 w-5" />
                      {t('auth:register.back')}
                    </button>
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:from-indigo-700 hover:to-purple-700 font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl"
                    >
                      {isLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          {t('auth:register.processing')}
                        </>
                      ) : (
                        <>
                          {selectedPlan?.slug === 'free'
                            ? t('auth:register.createAccount')
                            : t('auth:register.continueToPayment')}
                          <ArrowRightIcon className="h-5 w-5" />
                        </>
                      )}
                    </button>
                  </div>
                </form>

                <p className="text-center text-sm text-gray-600 dark:text-gray-400 mt-6">
                  {t('auth:register.haveAccount')}{' '}
                  <Link
                    to="/login"
                    className="text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 font-bold transition-colors"
                  >
                    {t('auth:register.login')}
                  </Link>
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
